/**
 * React Query hooks for QMS Board
 */

import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  fetchQmsBoard,
  moveQmsTicket,
  deleteQmsTicket,
} from '../api/qmsApi';
import type { QmsBoardResponse, QmsTicket, QmsMoveTicketPayload, QmsBoard, QmsTicketType } from '../types/qms.types';
import { getQmsColumnKey } from '../constants/qmsBoard';

/**
 * Query key factory for QMS
 */
export const qmsKeys = {
  all: ['qms'] as const,
  boards: () => [...qmsKeys.all, 'board'] as const,
  board: (organisation: string, options: UseQmsBoardOptions = {}) => [
    ...qmsKeys.boards(),
    organisation,
    options.type || 'all',
    options.accountId || 'all',
  ] as const,
  tickets: () => [...qmsKeys.all, 'tickets'] as const,
  ticket: (ticketId: string) => [...qmsKeys.tickets(), ticketId] as const,
  comments: (ticketId: string) => [...qmsKeys.ticket(ticketId), 'comments'] as const,
  timeline: (ticketId: string) => [...qmsKeys.ticket(ticketId), 'timeline'] as const,
};

interface UseQmsBoardOptions {
  type?: QmsTicketType;
  accountId?: string;
}

/**
 * Fetch QMS Board
 */
export function useQmsBoard(organisation: string, options: UseQmsBoardOptions = {}) {
  return useQuery({
    queryKey: qmsKeys.board(organisation, options),
    queryFn: () => fetchQmsBoard(organisation, options),
    enabled: !!organisation,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

/** Mutation variables type for move ticket */
interface MoveTicketVariables {
  ticketId: string;
  payload: QmsMoveTicketPayload;
}

/** Context type for optimistic update rollback */
interface MoveTicketContext {
  previousBoards: [QueryKey, QmsBoardResponse | undefined][];
}

export function moveQmsTicketInBoardCache(
  old: QmsBoardResponse | undefined,
  ticketId: string,
  payload: QmsMoveTicketPayload,
  serverTicket?: QmsTicket,
): QmsBoardResponse | undefined {
  if (!old?.columns) return old;

  const updatedColumns: QmsBoard = {};
  let movedTicket: QmsTicket | null = serverTicket
    ? { ...serverTicket, status: payload.status, position: payload.position }
    : null;

  for (const [columnKey, tickets] of Object.entries(old.columns)) {
    updatedColumns[columnKey] = tickets.filter((ticket) => {
      if (ticket._id !== ticketId) return true;
      movedTicket = {
        ...ticket,
        ...serverTicket,
        status: payload.status,
        position: payload.position,
      };
      return false;
    });
  }

  if (!movedTicket) return old;

  const targetColumnKey = getQmsColumnKey(movedTicket.type, payload.status);
  updatedColumns[targetColumnKey] = [
    ...(updatedColumns[targetColumnKey] || []),
    movedTicket,
  ];

  const counts = Object.fromEntries(
    Object.entries(updatedColumns).map(([key, tickets]) => [key, tickets.length])
  );

  return { ...old, columns: updatedColumns, counts };
}

/**
 * Move ticket mutation (drag & drop) with optimistic updates
 */
export function useMoveQmsTicket() {
  const queryClient = useQueryClient();

  return useMutation<QmsTicket, Error, MoveTicketVariables, MoveTicketContext>({
    mutationFn: ({ ticketId, payload }) => moveQmsTicket(ticketId, payload),

    // Optimistic update
    onMutate: async ({ ticketId, payload }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: qmsKeys.boards() });

      // Snapshot the previous value
      const previousBoards = queryClient.getQueriesData<QmsBoardResponse>({
        queryKey: qmsKeys.boards(),
      });

      // Optimistically update all board caches
      queryClient.setQueriesData<QmsBoardResponse>(
        { queryKey: qmsKeys.boards() },
        (old) => moveQmsTicketInBoardCache(old, ticketId, payload)
      );

      // Return context with snapshot
      return { previousBoards };
    },

    onSuccess: (ticket, { ticketId, payload }) => {
      queryClient.setQueriesData<QmsBoardResponse>(
        { queryKey: qmsKeys.boards() },
        (old) => moveQmsTicketInBoardCache(old, ticketId, payload, ticket)
      );
      queryClient.setQueryData(qmsKeys.ticket(ticketId), ticket);
    },

    // Rollback on error
    onError: (_err, _variables, context) => {
      if (context?.previousBoards) {
        context.previousBoards.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },

    // Refetch on success to sync with server
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
    },
  });
}

/**
 * Delete ticket mutation
 */
export function useDeleteQmsTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => deleteQmsTicket(ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qmsKeys.boards() });
      queryClient.invalidateQueries({ queryKey: qmsKeys.tickets() });
    },
  });
}
